/**
 * API client for the ported ai4swe-secops-app onboarding wizard, adapted to hit
 * Sentinel's backend (/config/onboarding-secops/*) instead of ai4swe-secops-service's
 * Postgres-backed /onboarding endpoints. Connection validators (GHE/Sonar/BlackDuck/Trivy)
 * are validated directly by Sentinel's backend — no ai4swe-secops-service dependency.
 */
import sentinelNetwork from '../../lib/api/sentinelNetwork';
import { triggerScan as triggerScanService } from '../../lib/api/scanService';
import { encryptString, fetchPublicKey } from '@/lib/crypto';

export interface OnboardingListItem {
  id: string;
  appId: string;
  project_id: string;
  config_name: string;
  appName?: string;
  leanIXId?: string;
  version: number;
  checksum: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  project_phase?: string;
  /** True when the calling user is already a member of this project. */
  is_member?: boolean;
}

export interface OnboardingDetail extends OnboardingListItem {
  yaml_content: string;
  members?: { email: string; role: string }[];
  /** Management access entries embedded by the backend to avoid a separate fetch that could fail silently. */
  managementAccess?: { email: string; managementLevel: string }[];
}

export interface OnboardingPayload {
  config_name: string;
  yaml_content: string;
  members?: { email: string; role: string }[];
}

export async function fetchOnboardings(): Promise<OnboardingListItem[]> {
  const { data } = await sentinelNetwork.get<OnboardingListItem[]>('/config/onboarding-secops');
  return data;
}

export async function fetchOnboardingDetail(appId: string): Promise<OnboardingDetail> {
  const { data } = await sentinelNetwork.get<OnboardingDetail>(`/config/onboarding-secops/${appId}`);
  return data;
}

/** Find already-onboarded projects by LeanIX ID — used by the pre-access onboarding search step. */
export async function searchOnboardingsByLeanIx(leanIxId: string): Promise<OnboardingListItem[]> {
  const { data } = await sentinelNetwork.get<OnboardingListItem[]>('/config/onboarding-secops/search', {
    params: { leanIxId },
  });
  return data;
}

export async function createOnboarding(payload: OnboardingPayload): Promise<{ data: { appId: string; project_id: string; config_name: string } }> {
  const { data } = await sentinelNetwork.post('/config/onboarding-secops', payload);
  return data;
}

export async function updateOnboarding(appId: string, payload: OnboardingPayload): Promise<{ data: { appId: string; project_id: string; config_name: string } }> {
  const { data } = await sentinelNetwork.put(`/config/onboarding-secops/${appId}`, payload);
  return data;
}

/** Queues a full security scan for a project — used to auto-trigger a scan right after onboarding submission. */
export const triggerScan = triggerScanService;

/** Save a single management access entry for a project. */
export async function saveManagementAccessEntry(appId: string, entry: { email: string; managementLevel: string }): Promise<void> {
  await sentinelNetwork.post(`/config/project/${appId}/management-access`, entry);
}

/** Fetch all management access entries for a project. */
export async function fetchManagementAccessEntries(appId: string): Promise<{ email: string; managementLevel: string }[]> {
  const { data } = await sentinelNetwork.get(`/config/project/${appId}/management-access`);
  return data.data || [];
}

// ── GHE branch lookup — validated directly by Sentinel's backend ──

export async function fetchGheBranches(gheToken: string, repoUrl: string): Promise<string[]> {
  if (!gheToken || !repoUrl) return [];
  try {
    const encryptedToken = await encryptForBackend(gheToken);
    const { data } = await sentinelNetwork.post<string[]>('/config/onboarding-secops/validate/ghe/branches', { token: encryptedToken, repoUrl });
    return data ?? [];
  } catch (err) {
    console.error('[onboarding-secops API] fetchGheBranches failed:', err);
    return [];
  }
}

// ── Tool connection validation — validated directly by Sentinel's backend ──

/** Encrypt a sensitive value with the RSA public key before sending to the backend. */
async function encryptForBackend(value: string): Promise<string> {
  if (!value) return value;
  const pubKey = await fetchPublicKey();
  return encryptString(value, pubKey);
}

export interface ValidationResult { ok: boolean; message: string }

export async function validateGheConnection(gheToken: string, gheBaseUrl: string): Promise<ValidationResult> {
  try {
    const encryptedToken = await encryptForBackend(gheToken);
    const { data } = await sentinelNetwork.post<ValidationResult>('/config/onboarding-secops/validate/ghe', { token: encryptedToken, repoUrl: gheBaseUrl });
    return data;
  } catch (e: any) {
    return { ok: false, message: e?.message || 'Connection failed' };
  }
}

export async function validateSonarConnection(sonarUrl: string, sonarToken: string): Promise<ValidationResult> {
  try {
    const encryptedToken = await encryptForBackend(sonarToken);
    const { data } = await sentinelNetwork.post<ValidationResult>('/config/onboarding-secops/validate/sonar', { url: sonarUrl, token: encryptedToken });
    return data;
  } catch (e: any) {
    return { ok: false, message: e?.message || 'Connection failed' };
  }
}

export async function validateBlackDuckConnection(bdUrl: string, bdToken: string): Promise<ValidationResult> {
  try {
    const encryptedToken = await encryptForBackend(bdToken);
    const { data } = await sentinelNetwork.post<ValidationResult>('/config/onboarding-secops/validate/blackduck', { url: bdUrl, token: encryptedToken });
    return data;
  } catch (e: any) {
    return { ok: false, message: e?.message || 'Connection failed' };
  }
}

export async function validateTrivyConnection(registryUrl: string, username: string, secret: string): Promise<ValidationResult> {
  try {
    const encryptedSecret = await encryptForBackend(secret);
    const { data } = await sentinelNetwork.post<ValidationResult>('/config/onboarding-secops/validate/trivy', { registryUrl, username, secret: encryptedSecret });
    return data;
  } catch (e: any) {
    return { ok: false, message: e?.message || 'Connection failed' };
  }
}

/**
 * Validates a Polaris application UUID against Sentinel's centrally managed service
 * account. Unlike the other tools, no credential is ever sent from the browser —
 * Sentinel authenticates to Polaris using one server-side service-account token.
 */
export async function validatePolarisConnection(applicationId: string): Promise<ValidationResult> {
  try {
    const { data } = await sentinelNetwork.post<ValidationResult>('/config/onboarding-secops/validate/polaris', { applicationId });
    return data;
  } catch (e: any) {
    return { ok: false, message: e?.message || 'Connection failed' };
  }
}
